import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Badge,
  Tooltip,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  ButtonBase,
  Avatar,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Dashboard as DashboardIcon,
  Domain as TenantsIcon,
  ChevronRight as ChevronIcon,
  ViewSidebar as CollapseIcon,
} from '@mui/icons-material';
import { menuGroups, sidebarSections, itemIcons, getGroupItems } from './navConfig';
import { useTerms } from '../context/TerminologyContext';

const WIDTH = 236;
const RAIL_WIDTH = 68;
const COLLAPSE_KEY = 'hourtrax-sidebar-collapsed';

// Persistent desktop sidebar (docs/ui-refresh-mockup.html): brand header,
// grouped sections with expandable sub-items, active-item accent highlight,
// user footer, collapsible to an icon rail (groups open as popover menus).

function NavItemButton({ icon, label, active, indent = 0, badge = 0, trailing, onClick, collapsed, tooltip }) {
  const btn = (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 1.25,
        px: collapsed ? 0 : 1,
        py: 0.875,
        pl: collapsed ? 0 : 1 + indent,
        borderRadius: '6px',
        fontFamily: 'inherit',
        fontSize: '0.8125rem',
        fontWeight: active ? 600 : 500,
        color: active ? 'primary.dark' : 'text.secondary',
        bgcolor: active ? 'primary.soft' : 'transparent',
        transition: 'background-color .12s, color .12s',
        '&:hover': { bgcolor: active ? 'primary.soft' : 'background.surface2', color: active ? 'primary.dark' : 'text.primary' },
        '& svg': { fontSize: 19 },
      }}
    >
      {badge > 0 ? (
        <Badge badgeContent={badge} color="error" max={99} variant={collapsed ? 'dot' : 'standard'}>
          {icon}
        </Badge>
      ) : icon}
      {!collapsed && <Box component="span" sx={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>}
      {!collapsed && trailing}
    </ButtonBase>
  );
  return collapsed && tooltip ? <Tooltip title={tooltip} placement="right">{btn}</Tooltip> : btn;
}

export default function Sidebar({
  tabs,
  currentTab,
  onNavigate,
  roleFlags,
  roleLabel,
  tenantName,
  user,
  pendingApprovalCount = 0,
  pendingPTOCount = 0,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState({});
  const [railMenu, setRailMenu] = useState(null); // { key, anchorEl }
  const { navLabel } = useTerms();

  const currentLabel = tabs[currentTab]?.label;
  const availableTabLabels = useMemo(() => new Set(tabs.map(t => t.label)), [tabs]);

  const groupItems = (key) => getGroupItems(key, roleFlags, availableTabLabels);

  const findTabIndex = (label) => tabs.findIndex(t => t.label === label);

  const itemBadge = (label) => {
    if (label === 'Approvals') return pendingApprovalCount;
    if (label === 'Time Off') return pendingPTOCount;
    return 0;
  };

  const groupBadge = (key) =>
    groupItems(key).reduce((sum, item) => sum + itemBadge(item), 0);

  // Auto-expand the group containing the active item
  useEffect(() => {
    if (!currentLabel) return;
    for (const key of Object.keys(menuGroups)) {
      if (groupItems(key).includes(currentLabel)) {
        setExpanded(prev => (prev[key] ? prev : { ...prev, [key]: true }));
        break;
      }
    }
  }, [currentLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      try { localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1'); } catch { /* session-only */ }
      return !prev;
    });
    setRailMenu(null);
  };

  const navigateTo = (label) => {
    const idx = findTabIndex(label);
    if (idx >= 0) onNavigate(idx);
    setRailMenu(null);
  };

  const handleGroupClick = (key) => (event) => {
    if (collapsed) {
      setRailMenu({ key, anchorEl: event.currentTarget });
    } else {
      setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const initials = ((user?.firstName?.[0] || user?.username?.[0] || '?') + (user?.lastName?.[0] || '')).toUpperCase();

  const renderGroup = (key) => {
    const group = menuGroups[key];
    const items = groupItems(key);
    if (items.length === 0) return null;
    const isOpen = !!expanded[key];
    const activeInGroup = items.includes(currentLabel);

    return (
      <React.Fragment key={key}>
        <NavItemButton
          icon={group.icon}
          label={group.label}
          active={activeInGroup && (!isOpen || collapsed)}
          badge={groupBadge(key)}
          collapsed={collapsed}
          tooltip={group.label}
          onClick={handleGroupClick(key)}
          trailing={
            <ChevronIcon sx={{ fontSize: 15, opacity: 0.6, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
          }
        />
        {!collapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', my: '1px' }}>
              {items.map(item => (
                <NavItemButton
                  key={item}
                  icon={itemIcons[item] || <DashboardIcon />}
                  label={navLabel(item)}
                  indent={2}
                  active={currentLabel === item}
                  badge={itemBadge(item)}
                  onClick={() => navigateTo(item)}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={{
        width: collapsed ? RAIL_WIDTH : WIDTH,
        flex: 'none',
        transition: 'width .2s',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 1.25, px: collapsed ? 0 : 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', flex: 'none' }}>
          <ClockIcon sx={{ fontSize: 17 }} />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 650, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>HourTrax</Typography>
            {tenantName && (
              <Typography noWrap sx={{ fontSize: '0.6875rem', color: 'text.faint' }}>{tenantName}</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Nav. Direct children must not flex-shrink: MUI Collapse sets an
          inline min-height: 0, so on short viewports the expanded groups
          would collapse to a few px and their items would paint over the
          next section instead of scrolling. */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: collapsed ? 1 : 1.25,
          py: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          '& > *': { flexShrink: 0 },
        }}
      >
        <NavItemButton
          icon={<DashboardIcon />}
          label="Home"
          active={currentLabel === 'Dashboard'}
          collapsed={collapsed}
          tooltip="Home"
          onClick={() => navigateTo('Dashboard')}
        />
        {roleFlags.isProgramAdmin && availableTabLabels.has('Tenants') && (
          <NavItemButton
            icon={<TenantsIcon />}
            label="Tenants"
            active={currentLabel === 'Tenants'}
            collapsed={collapsed}
            tooltip="Tenants"
            onClick={() => navigateTo('Tenants')}
          />
        )}

        {sidebarSections.map(section => {
          const visibleGroups = section.groups.filter(key => groupItems(key).length > 0);
          if (visibleGroups.length === 0) return null;
          // Employees reach the Help tab through this section but nothing
          // else in it — labeling it "Admin" for them would be misleading.
          const sectionItems = visibleGroups.flatMap(key => groupItems(key));
          const sectionLabel = sectionItems.every(item => item === 'Help') ? 'Help' : section.label;
          return (
            <React.Fragment key={section.label}>
              {!collapsed ? (
                <Typography sx={{ fontSize: '0.6875rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.faint', fontWeight: 600, px: 1, pt: 1.75, pb: 0.5 }}>
                  {sectionLabel}
                </Typography>
              ) : (
                <Box sx={{ borderTop: 1, borderColor: 'divider', mx: 1, my: 1 }} />
              )}
              {visibleGroups.map(renderGroup)}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Rail-mode group popover */}
      <Menu
        open={!!railMenu}
        anchorEl={railMenu?.anchorEl}
        onClose={() => setRailMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {railMenu && groupItems(railMenu.key).map(item => (
          <MenuItem key={item} selected={currentLabel === item} onClick={() => navigateTo(item)} sx={{ minWidth: 180 }}>
            <ListItemIcon>{itemIcons[item] || <DashboardIcon />}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>{navLabel(item)}</ListItemText>
            {itemBadge(item) > 0 && (
              <Badge badgeContent={itemBadge(item)} color="error" max={99} sx={{ ml: 2 }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Footer */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', px: collapsed ? 0 : 1.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 1.25 }}>
        {!collapsed && (
          <>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.soft', color: 'primary.dark', fontSize: '0.6875rem', fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}>
                {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.username}
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.6875rem', color: 'text.faint' }}>{roleLabel}</Typography>
            </Box>
          </>
        )}
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton size="small" onClick={toggleCollapsed} sx={{ color: 'text.faint', border: 1, borderColor: 'divider', borderRadius: '6px' }}>
            <CollapseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
